import * as React from 'react';
import classnames from 'classnames';

import ActionButton from 'components/buttons/action-button/ActionButton';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { expandDataProps } from 'utils/helper/common';

import './checkboxes-list.scss';

import type { CheckboxesListItem, Data } from 'types';

type ActionType = {
  label: string,
  onClick: () => void
};

type Props<T> = {
  lists: Array<T>,
  highlightedId?: number | string,
  emptyLabel: string,
  size?: 'small' | 'normal',
  customContainerClass?: string,
  customItemClass?: string,
  onCheckboxChange?: (b: React.SyntheticEvent<HTMLElement>, a: T) => void,
  onCheckboxRemoved?: (b: React.SyntheticEvent<HTMLElement>, a: T) => void,
  disabled?: boolean,
  label?: string,
  action?: ActionType,
  labelComponent?: (a: T) => React.ReactNode,
  data?: Data,
  isHorizontalList?: boolean
};

class CheckboxesList<T extends CheckboxesListItem> extends React.PureComponent<Props<T>> {
  static defaultProps = {
    size: 'normal'
  };

  handleCheckboxChanged = (event: React.SyntheticEvent<HTMLElement>) => {
    const { onCheckboxChange, lists } = this.props;

    const target = event.currentTarget;
    const arg = target.dataset.arg;
    const itemId = isNaN(Number(arg)) ? arg : Number(arg);
    const item = lists.find(i => i.id === itemId);

    if (item && onCheckboxChange) {
      onCheckboxChange(event, item);
    }
  };

  handleCheckboxRemoved = (event: React.SyntheticEvent<HTMLElement>) => {
    const { onCheckboxRemoved, lists } = this.props;

    const target = event.currentTarget;
    const arg = target.dataset.arg;
    const itemId = isNaN(Number(arg)) ? arg : Number(arg);
    const item = lists.find(i => i.id === itemId);

    if (item && onCheckboxRemoved) {
      onCheckboxRemoved(event, item);
    }

    event.stopPropagation();
  };

  handleItemActionClicked = (event: Event) => {
    event.stopPropagation();
  };

  render(): React.ReactNode {
    const {
      lists,
      emptyLabel,
      size,
      onCheckboxRemoved,
      disabled,
      label,
      labelComponent,
      highlightedId,
      action,
      customContainerClass,
      customItemClass,
      data,
      isHorizontalList
    } = this.props;

    const containerClasses = classnames({
      'checkboxes-list': true,
      'checkboxes-list--horizontal': isHorizontalList,
      'checkboxes-list--disabled': disabled,
      [`checkboxes-list--size-${String(size)}`]: size,
      [`${String(customContainerClass)}`]: Boolean(customContainerClass)
    });

    const listClasses = classnames({
      'checkboxes-list__list': true,
      'checkboxes-list__list--horizontal': isHorizontalList,
      [`checkboxes-list__list--size-${String(size)}`]: size
    });

    return (
      <div className={containerClasses}>
        {
          label && (
            <div className="checkboxes-list__label">
              {label}
            </div>
          )
        }
        <ul className={listClasses}>
          {
            lists.map(item => {
              const itemClasses = classnames({
                'checkboxes-list__item': true,
                'checkboxes-list__item--disabled': item.isDisabled,
                'checkboxes-list__item--highlighted': highlightedId === item.id,
                [String(customItemClass)]: customItemClass
              });

              const itemInfoClasses = classnames({
                'checkboxes-list__item-info': true,
                'checkboxes-list__item-info--disabled': item.isDisabled
              });

              const checkboxClasses = classnames({
                'checkboxes-list__checkbox': true,
                'checkboxes-list__checkbox--selected': item.isSelected,
                [`checkboxes-list__checkbox--size-${String(size)}`]: size
              });

              const iconClasses = classnames({
                'checkboxes-list__icon': true,
                'checkboxes-list__icon--selected': item.isSelected,
                [`checkboxes-list__icon--size-${String(size)}`]: size
              });

              const labelClasses = classnames({
                'checkboxes-list__item-label': true,
                [`checkboxes-list__item-label--size-${String(size)}`]: size
              });

              const actionClasses = classnames({
                'checkboxes-list__item-action': true,
                'checkboxes-list__item-action--hidden': item.action && Boolean(item.action.hidden)
              });

              return (
                <li
                  className={itemClasses}
                  key={`checkboxes-list-${item.id}`}
                  data-arg={item.id}
                  {...expandDataProps(data)}
                  onMouseDown={this.handleCheckboxChanged}
                >
                  <div className={itemInfoClasses}>
                    <div className={checkboxClasses}>
                      <div className={iconClasses}>
                        <SVGDynamicIcon
                          name="checkmark-custom-fg"
                          width={12}
                          height={10}
                          customColor="primary"
                        />
                      </div>
                    </div>
                    <div
                      className={labelClasses}
                      title={(typeof item.label === 'string') ? item.label : ''}
                      data-qa="checkboxes-list__checkbox-label"
                    >
                      {labelComponent ? labelComponent(item) : item.label}
                    </div>
                  </div>
                  <div>
                    {
                      onCheckboxRemoved &&
                        <div
                          className="checkboxes-list__item-cross"
                          data-arg={item.id}
                          onClick={this.handleCheckboxRemoved}
                        >
                          <SVGDynamicIcon
                            name="cross-custom-fg"
                            width={8}
                            height={8}
                            customColor="grey"
                          />
                        </div>
                    }
                    {
                      item.action &&
                        <div
                          className={actionClasses}
                          data-qa={actionClasses}
                        >
                          <ActionButton
                            type="secondary"
                            link={item.action.link}
                            onClick={this.handleItemActionClicked}
                            newTab
                          >
                            {item.action.label}
                          </ActionButton>
                        </div>
                    }
                  </div>
                </li>
              );
            })
          }
          {
            lists.length === 0 && (
              <li className="checkboxes-list__item checkboxes-list__item--empty">
                {emptyLabel}
              </li>
            )
          }
          {
            action && (
              <li className="checkboxes-list__item checkboxes-list__item--action" onClick={action.onClick}>
                {action.label}
              </li>
            )
          }
        </ul>
      </div>
    );
  }
}

export default CheckboxesList;
